// src/encounter/ips-note-dialog.tsx
// "+ Note" dialog for the IPS-rail Active Problems section (PROD-1489 BRD row action).
// PRESENTATIONAL — composes `AddSurface` + DS TextArea, mirroring the sibling Ips*Dialog pattern.
// `AddSurface` (not `Modal` directly) so this dialog follows its siblings: rendered inside a rail
// popover it emits the anchored body, rendered anywhere else the centred `Modal` exactly as before.
// Ahmed, 2026-09-07: the Active Problems row action "+ Note" is the only left-column control whose
// label starts with a plus, and it was the last one still raising a centred modal.
// SINGLE-note semantics (Flutter parity: one scalar `note` per condition, edited in place) —
// the field PREFILLS the existing note so saving can never silently clobber a prior note.
import { useState } from 'react';
import { TextArea } from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';

export interface IpsNoteDialogProps {
  open: boolean;
  /** The condition's display label (dialog subtitle context). */
  subject: string;
  /** Current note — prefilled so the edit is in-place, never a blind overwrite. */
  note: string;
  saving?: boolean;
  onSubmit: (note: string) => void;
  onClose: () => void;
  /** Words only (left-rail target §3/§4): the same surface asks for an appended note or a required reason. Defaults: today's. */
  words?: { title?: string; field?: string; commit?: string; placeholder?: string };
  /** The commit is refused while the text is empty (a required reason). */
  required?: boolean;
}

export function IpsNoteDialog({ open, subject, note, saving, onSubmit, onClose, words, required }: IpsNoteDialogProps) {
  const [value, setValue] = useState(note);
  if (!open) return null;
  const commitWord = words?.commit ?? 'Save note';
  return (
    <AddSurface
      open
      onClose={onClose}
      title={words?.title ?? (note ? 'Edit note' : 'Add note')}
      size="sm"
      commit={{
        run: () => {
          if (required && !value.trim()) return;
          onSubmit(value);
        },
        label: commitWord,
        text: saving ? 'Saving…' : commitWord,
        busy: saving,
      }}
    >
      <div className="font-sans">
        <TextArea
          label={`${words?.field ?? 'Note'} — ${subject}`}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          rows={4}
          placeholder={words?.placeholder ?? 'Clinical note for this problem…'}
          disabled={saving}
        />
      </div>
    </AddSurface>
  );
}
